<template>
  <section v-show="fragmentsLoaded" class="eptp_content">
    <div class="content_top">
      <div class="container">
        <ul class="breadcrumbs">
          <li>
            <a :href="homeHref">{{ $t('pages.home.title') }}</a>
          </li>
          <li>
            <a :href="$url($t('pages.specialistSearch.url'))">{{
              $t('pages.specialistSearch.title')
            }}</a>
          </li>
        </ul>

        <ul class="nav_cat clearfix">
          <li class="nav_cat__items">
            <a
              :href="$t('pages.specialistSearch.links.registeredTpdSearch.url')"
              class="nav_cat__links"
              target="_blank"
              >{{ $t('pages.specialistSearch.links.registeredTpdSearch.label') }}</a
            >
          </li>
          <li class="nav_cat__items">
            <a
              :href="$t('pages.specialistSearch.links.preparatoryTpdSearch.url')"
              class="nav_cat__links"
              target="_blank"
              >{{ $t('pages.specialistSearch.links.preparatoryTpdSearch.label') }}</a
            >
          </li>
          <li class="nav_cat__items">
            <a
              :href="burl()"
              class="nav_cat__links"
              target="_blank"
              >{{ $t('pages.specialistSearch.links.buildingPermitSearch.label') }}</a
            >
          </li>
        </ul>
      </div>
    </div>

    <div class="container">
      <BaseGlobalAlert />

      <h1>
        {{ $t('pages.specialistSearch.title') }}
      </h1>

      <form class="clearfix" @submit.prevent="getSpecialists(1)">
        <ul class="filter_form clearfix">
          <li class="filter_form__items">
            <label class="filter_form__label" for="license-type-filter">{{
              $t('pages.specialistSearch.filters.licType')
            }}</label>
            <BaseSelect
              v-model="specialistParams.licType"
              :options="licenseTypes"
              :search-function="getLicenseTypes"
              option-value-key="id"
              id="license-type-filter"
              clearable
              searchable
            />
          </li>
          <li class="filter_form__items">
            <label class="filter_form__label" for="license-institution-filter">{{
              $t('pages.specialistSearch.filters.licInst')
            }}</label>
            <BaseSelect
              v-model="specialistParams.licInst"
              :options="licenseInstitutions"
              :search-function="getLicenseInstitutions"
              option-value-key="id"
              id="license-institution-filter"
              clearable
              searchable
            />
          </li>
          <li class="filter_form__items">
            <label class="filter_form__label" for="license-class-filter">{{
              $t('pages.specialistSearch.filters.licClass')
            }}</label>
            <BaseSelect
              v-model="specialistParams.licClass"
              :options="licenseClasses"
              :search-function="getLicenseClasses"
              option-value-key="id"
              id="license-class-filter"
              clearable
              searchable
            />
          </li>
          <li class="filter_form__items">
            <label class="filter_form__label" for="license-status-filter">{{
              $t('pages.specialistSearch.filters.licStatus')
            }}</label>
            <BaseSelect
              v-model="specialistParams.licStatus"
              :options="licenseStatuses"
              :search-function="getLicenseStatuses"
              option-value-key="id"
              id="license-status-filter"
              clearable
              searchable
            />
          </li>
          <li class="filter_form__items">
            <label class="filter_form__label" for="license-activity-filter">{{
              $t('pages.specialistSearch.filters.licActivity')
            }}</label>
            <BaseSelect
              v-model="specialistParams.licActivity"
              :options="licenseActivities"
              :search-function="getLicenseActivities"
              option-value-key="id"
              id="license-activity-filter"
              clearable
              searchable
            />
          </li>
          <li class="filter_form__items">
            <label for="license-number-filter" class="filter_form__label">{{
              $t('pages.specialistSearch.filters.licNumber')
            }}</label>
            <input
              v-model="specialistParams.licNumber"
              type="text"
              name="text"
              id="license-number-filter"
            />
          </li>
          <li class="filter_form__items">
            <label class="filter_form__label" for="recipient-type-filter">{{
              $t('pages.specialistSearch.filters.recipientFilter')
            }}</label>
            <BaseSelect
              v-model="specialistParams.recipientType"
              :options="recipientFilterOptions"
              :reduce="(option) => option.value"
              option-value-key="value"
              option-label-key="label"
              id="recipient-type-filter"
              clearable
              @input="resetHiddenFilters"
            />
          </li>

          <li class="filter_form__items">
            <div v-for="recipient in recipientFilterOptions" :key="recipient.value">
              <template v-if="recipient.value === specialistParams.recipientType">
                <p v-for="filter in recipient.filters" :key="filter" class="recipient-type-filter">
                  <label :for="`${filter}-filter`" class="filter_form__label">{{
                    $t(`pages.specialistSearch.filters.${filter}`)
                  }}</label>
                  <input
                    v-model="specialistParams[filter]"
                    :id="`${filter}-filter`"
                    type="text"
                    name="text"
                  />
                </p>
              </template>
            </div>
          </li>

          <li class="filter_form__items filter_form__items--full">
            <button
              :disabled="loadingSpecialists"
              type="submit"
              class="btn btn-feat btn-mobile-full pull-right"
            >
              {{ $t(`actions.${hasSearched ? 'updateResults' : 'search'}`) }}
            </button>
          </li>
        </ul>
      </form>

      <BaseTable
        v-if="hasSearched || loadingSpecialists"
        :headers="tableHeaders"
        :items="specialists"
        :loading="loadingSpecialists"
        :pagination="specialistPagination"
        :on-row-click="viewSpecialist"
        @update:page="getSpecialists"
      >
        <template #item.recipient="{ item }">
          <div v-for="(recipient, index) in item.recipient" :key="index">
            {{ getRecipientString(recipient) }}
          </div>
        </template>
      </BaseTable>
    </div>

    <ImportantMessagesModal
      :loading="loadingImportantMessage"
      :message="displayedImportantMessage"
      @read="markImportantMessageAsRead"
    />
  </section>
</template>

<script>
import queryString from 'query-string';
import BaseSelect from '../../components/base/BaseSelect';
import pageMixin from '../../mixins/page.mixin';
import classifierService from '../../services/classifier.service';
import specialistService from '../../services/specialist.service';
import BaseTable from '../../components/base/BaseTable';
import BaseGlobalAlert from '../../components/base/BaseGlobalAlert';
import { getQueryParam, updateUrl } from '../../util/url';

const ALLOWED_OBJECT_FILTERS = ['licActivity', 'licClass', 'licInst', 'licStatus', 'licType'];
const ALLOWED_STRING_FILTERS = [
  'recipientType',
  'licNumber',
  'firstname',
  'lastname',
  'personCode',
  'personCountryCode',
  'institutionName',
  'institutionCode',
  'institutionCountryCode',
];

export default {
  name: 'SpecialistSearch',

  components: { BaseGlobalAlert, BaseTable, BaseSelect },

  mixins: [pageMixin],

  data() {
    return {
      loadingSpecialists: false,
      specialists: [],
      specialistPagination: {},
      specialistParams: {},
      licenseTypes: [],
      licenseActivities: [],
      licenseClasses: [],
      licenseStatuses: [],
      licenseInstitutions: [],
      hasSearched: false,
    };
  },

  computed: {
    tableHeaders() {
      const translationKey = 'pages.specialistSearch.table.headers';
      return [
        { text: this.$t(`${translationKey}.licenceNo`), value: 'licenceNo' },
        { text: this.$t(`${translationKey}.type`), value: 'type' },
        { text: this.$t(`${translationKey}.recipient`), value: 'recipient' },
        { text: this.$t(`${translationKey}.status`), value: 'status' },
        { text: this.$t(`${translationKey}.validFrom`), value: 'validFrom' },
        { text: this.$t(`${translationKey}.validTo`), value: 'validTo' },
      ];
    },

    recipientFilterOptions() {
      const translationKey = 'pages.specialistSearch.filters.recipientFilterOptions';
      return [
        {
          label: this.$t(`${translationKey}.ltPerson`),
          value: 'ltPerson',
          filters: ['firstname', 'lastname', 'personCode'],
        },
        {
          label: this.$t(`${translationKey}.ltInst`),
          value: 'ltInst',
          filters: ['institutionCode'],
        },
        {
          label: this.$t(`${translationKey}.foreignPerson`),
          value: 'foreignPerson',
          filters: ['firstname', 'lastname', 'personCountryCode'],
        },
        {
          label: this.$t(`${translationKey}.foreignInst`),
          value: 'foreignInst',
          filters: ['institutionName', 'institutionCountryCode'],
        },
      ];
    },
  },

  created() {
    document.title = `EPTP - ${this.$t('pages.specialistSearch.title')}`;
    this.specialistParams = this.parseUrlParams();
    const page = getQueryParam('page');
    if (Object.keys(this.specialistParams).length || page > 1) {
      this.getSpecialists(page);
    }
  },

  mounted() {
    this.setupPage();
    window.addEventListener('popstate', this.onPopState);
  },

  beforeDestroy() {
    window.removeEventListener('popstate', this.onPopState);
  },

  methods: {
    async getLicenseActivities(search = '') {
      const res = await classifierService.getLicenseActivities({ search });
      this.licenseActivities = res.data.licenceActivity;
    },

    async getLicenseClasses(search = '') {
      const res = await classifierService.getLicenseClasses({ search });
      this.licenseClasses = res.data.licenceClass;
    },

    async getLicenseInstitutions(search = '') {
      const res = await classifierService.getLicenseInstitutions({ search });
      this.licenseInstitutions = res.data.licenceInstitution;
    },

    async getLicenseStatuses(search = '') {
      const res = await classifierService.getLicenseStatuses({ search });
      this.licenseStatuses = res.data.licenceStatus;
    },

    async getLicenseTypes(search = '') {
      const res = await classifierService.getLicenseTypes({ search });
      this.licenseTypes = res.data.licenceType;
    },

    getRecipientString(recipient) {
      let string = recipient.name;
      if (recipient.type || recipient.countryCode) {
        string += ' (';
      }
      if (recipient.type) {
        string += recipient.type;
      }
      if (recipient.type && recipient.countryCode) {
        string += `, `;
      }
      if (recipient.countryCode) {
        string += recipient.countryCode;
      }
      if (recipient.type || recipient.countryCode) {
        string += ')';
      }
      return string;
    },

    getSpecialists(page = 1) {
      const params = {
        ...this.specialistParams,
        licActivity: this.specialistParams.licActivity?.id,
        licClass: this.specialistParams.licClass?.id,
        licInst: this.specialistParams.licInst?.id,
        licStatus: this.specialistParams.licStatus?.id,
        licType: this.specialistParams.licType?.id,
      };
      if (+page > 1) {
        params.page = page;
      } else {
        delete params.page;
      }

      updateUrl(this.getUrlParams({ ...this.specialistParams, page: params.page }));
      delete params.recipientType;
      this.loadingSpecialists = true;
      specialistService
        .getList(params)
        .then((res) => {
          this.specialists = res.data.specialist;
          this.specialistPagination = {
            page: res.data.page,
            total: res.data.total,
            pageSize: res.data.pageSize,
          };
        })
        .catch(() => {
          this.specialists = [];
          this.specialistPagination = {};
        })
        .finally(() => {
          this.loadingSpecialists = false;
          this.hasSearched = true;
        });
    },

    getUrlParams(params) {
      const urlParams = {};

      ALLOWED_OBJECT_FILTERS.forEach((key) => {
        if (params[key]) {
          urlParams[`${key}Id`] = params[key].id;
          urlParams[`${key}Name`] = params[key].name;
        }
      });

      ALLOWED_STRING_FILTERS.forEach((key) => {
        if (params[key]) {
          urlParams[key] = params[key];
        }
      });

      if (params.page) {
        urlParams.page = params.page;
      }

      return urlParams;
    },

    parseUrlParams() {
      const params = {};
      const urlParams = queryString.parse(window.location.search);

      Object.keys(urlParams).forEach((key) => {
        const objectKey = key.replace('Id', '');
        if (ALLOWED_OBJECT_FILTERS.indexOf(objectKey) > -1 && urlParams[`${objectKey}Name`]) {
          // both {object}Id and {object}Name fields are present in URL
          params[objectKey] = {
            id: +urlParams[`${objectKey}Id`],
            name: urlParams[`${objectKey}Name`],
          };
        }

        if (ALLOWED_STRING_FILTERS.indexOf(key) > -1) {
          params[key] = urlParams[key];
        }
      });

      return params;
    },

    onPopState() {
      this.specialistParams = this.parseUrlParams();
      const page = getQueryParam('page');
      this.getSpecialists(page);
    },

    viewSpecialist(specialist) {
      window.location.href = this.$url(`${this.$t('pages.specialist.url')}?id=${specialist.id}`);
    },

    resetHiddenFilters() {
      this.recipientFilterOptions.forEach((option) => {
        option.filters.forEach((filter) => {
          this.$delete(this.specialistParams, filter);
        });
      });
    },
    burl(){
      return window.vueAppConfig.buildingPermitSearchUrl;
    },
  },
};
</script>

<style>
@import '../../../assets/css/style.min.css';
@import '../../assets/index.css';

.recipient-type-filter {
  margin-bottom: 20px;
}
</style>
